// pages/SearchResults.tsx - Mobile optimized version
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, Filter, ChevronDown, Info, X } from 'lucide-react';
import { searchApi, type SearchResult } from '../../api/search';
import SearchProductCard from '../../components/customer/SearchProductCard';
import { useCategories } from '../../hooks/useCategories';

// Compact SearchTips component
const SearchTips: React.FC = () => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div className="bg-blue-50 rounded-lg p-3">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between text-blue-900 font-medium"
            >
                <div className="flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    <span className="text-sm">Search Tips</span>
                </div>
                <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>

            {isExpanded && (
                <ul className="mt-2 text-xs text-blue-800 space-y-1 pl-6">
                    <li>• Typos are automatically corrected</li>
                    <li>• Results ranked by relevance</li>
                    <li>• On-sale items are boosted</li>
                    <li>• Featured products appear first</li>
                </ul>
            )}
        </div>
    );
};

// Compact Filters component
const MobileFilters: React.FC<{
    categories: string[];
    selectedCategory: string;
    onCategoryChange: (category: string) => void;
}> = ({ categories, selectedCategory, onCategoryChange }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between text-gray-900"
            >
                <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4" />
                    <span className="text-sm font-medium">Filters</span>
                    {selectedCategory && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                            1
                        </span>
                    )}
                </div>
                <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>

            {isExpanded && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                    <h4 className="text-xs font-medium text-gray-700 mb-2">Categories</h4>
                    <div className="space-y-1">
                        <button
                            onClick={() => {
                                onCategoryChange('');
                                setIsExpanded(false);
                            }}
                            className={`w-full text-left px-2 py-1.5 rounded text-xs ${!selectedCategory ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                        >
                            All Categories
                        </button>
                        {categories.map(category => (
                            <button
                                key={category}
                                onClick={() => {
                                    onCategoryChange(category);
                                    setIsExpanded(false);
                                }}
                                className={`w-full text-left px-2 py-1.5 rounded text-xs truncate ${selectedCategory === category ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

interface SearchResultsProps {
    searchQuery?: string;
    initialCategory?: string;
}

const SearchResults: React.FC<SearchResultsProps> = ({
    searchQuery: propSearchQuery,
    initialCategory
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { categories, loading, isError } = useCategories();

    const urlParams = new URLSearchParams(location.search);
    const urlSearchQuery = urlParams.get('q') || '';
    const urlCategory = urlParams.get('category') || '';

    const searchQuery = propSearchQuery || urlSearchQuery;
    // Remove initialCategory if not needed, or sync with URL
    const [selectedCategory, setSelectedCategory] = useState<string>(() => {
        // Use initialCategory if provided, otherwise use URL category
        return initialCategory || urlCategory || '';
    });

    const [results, setResults] = useState<SearchResult[]>([]);
    // const [loading, setLoading] = useState(true);
    // const [categories, setCategories] = useState<string[]>([]);
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    useEffect(() => {
        // Update selectedCategory state when URL category changes
        if (urlCategory !== selectedCategory) {
            setSelectedCategory(urlCategory);
        }
    }, [urlCategory]); // Run when urlCategory changes
    // useEffect(() => {
    //   fetchResults();
    // }, [searchQuery, selectedCategory]); // This triggers when either changes

    useEffect(() => {
        const fetchResults = async () => {
            if (!searchQuery.trim()) {
                setResults([]);
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                // Use urlCategory directly from URL
                const data = await searchApi.advancedSearch({
                    q: searchQuery,
                    category: urlCategory || undefined // Use urlCategory here
                });

                const resultsArray = Array.isArray(data) ? data : (data?.results || []);
                setResults(resultsArray);
            } catch (error) {
                console.error('Search failed:', error);
                setResults([]);
            } finally {
                setLoading(false);
            }
        };

        fetchResults();
    }, [searchQuery, urlCategory]);

    useEffect(() => {
        searchApi.getSearchCategories()
            .then(cats => setCategories(cats || []))
            .catch(() => setCategories([]));
    }, []);

    // SYNC selectedCategory with URL when URL changes
    // useEffect(() => {
    //     // Only update if URL category is different from current state
    //     if (urlCategory !== selectedCategory) {
    //         setSelectedCategory(urlCategory);
    //     }
    // }, [location.search]); // Run when URL changes

    const fetchResults = async () => {
        if (!searchQuery.trim()) {
            setResults([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            // Pass the category from state (which is synced with URL)
            const data = await searchApi.advancedSearch({
                q: searchQuery,
                category: selectedCategory || undefined
            });

            const resultsArray = Array.isArray(data) ? data : (data?.results || []);
            setResults(resultsArray);
        } catch (error) {
            console.error('Search failed:', error);
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCategoryChange = (category: string) => {
        // Update URL
        const params = new URLSearchParams();
        params.set('q', searchQuery);
        if (category) {
            params.set('category', category);
        }
        navigate(`/search?${params.toString()}`);

        // Also update state immediately for responsive UI
        // setSelectedCategory(category);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 p-4 pt-20">
                <div className="max-w-7xl mx-auto">
                    <div className="animate-pulse space-y-4">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="bg-white rounded-lg h-24"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (!searchQuery.trim()) {
        return (
            <div className="min-h-screen bg-gray-50 p-4 pt-20">
                <div className="max-w-7xl mx-auto bg-white rounded-lg p-6 text-center">
                    <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">
                        Enter a search term
                    </h2>
                    <p className="text-gray-600 text-sm">
                        Type something in the search bar to find products
                    </p>
                </div>
            </div>
        );
    }

    const resultCount = results?.length || 0;

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Spacer for header */}
            <div className="pt-0 md:pt-5"></div>

            <div className="max-w-7xl mx-auto p-3 md:p-4">
                {/* Compact Search Header */}
                <div className="mb-4">
                    <h1 className="text-lg font-semibold text-gray-900 line-clamp-1">
                        Results for "{searchQuery}"
                    </h1>
                    <div className="flex items-center justify-between mt-1">
                        <p className="text-sm text-gray-600">
                            {resultCount} {resultCount === 1 ? 'product' : 'products'}
                            {selectedCategory && ` in ${selectedCategory}`}
                        </p>

                        {/* Mobile filter toggle */}
                        <button
                            onClick={() => setShowMobileFilters(!showMobileFilters)}
                            className="md:hidden flex items-center gap-1 text-sm text-blue-600"
                        >
                            <Filter className="h-4 w-4" />
                            Filters
                        </button>
                    </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-4">
                    {/* Desktop Sidebar - Hidden on mobile */}
                    <div className="hidden md:block md:w-56 space-y-4">
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <h3 className="font-medium text-gray-900 mb-3 text-sm">
                                Categories
                            </h3>
                            <div className="space-y-1">
                                <button
                                    onClick={() => handleCategoryChange('')}
                                    className={`w-full text-left px-3 py-2 rounded text-sm ${!selectedCategory ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                                >
                                    All Categories
                                </button>

                                {loading ? (
                                    <div className="py-2 text-center">
                                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                        <span className="ml-2 text-xs text-gray-500">Loading...</span>
                                    </div>
                                ) : isError ? (
                                    <div className="py-2 text-center text-xs text-red-500">
                                        Failed to load categories
                                    </div>
                                ) : (
                                    categories.map(category => (
                                        <button
                                            key={category}
                                            onClick={() => handleCategoryChange(category)}
                                            className={`w-full text-left px-3 py-2 rounded text-sm truncate ${selectedCategory === category ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                                        >
                                            {category}
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                        <SearchTips />
                    </div>

                    {/* Mobile Filters Overlay */}
                    {showMobileFilters && (
                        <div className="md:hidden fixed inset-0 z-50 bg-black bg-opacity-50">
                            <div className="absolute right-0 top-0 bottom-0 w-72 bg-white shadow-xl p-4 overflow-y-auto">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="font-semibold text-gray-900">Filters</h2>
                                    <button
                                        onClick={() => setShowMobileFilters(false)}
                                        className="p-1 hover:bg-gray-100 rounded"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>
                                <MobileFilters
                                    categories={categories}
                                    selectedCategory={selectedCategory}
                                    onCategoryChange={handleCategoryChange}
                                />
                                <div className="mt-4">
                                    <SearchTips />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Results Section */}
                    <div className="flex-1">
                        {resultCount === 0 ? (
                            <div className="bg-white rounded-lg p-6 text-center">
                                <Search className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                                <h3 className="font-medium text-gray-900 mb-2">
                                    No products found
                                </h3>
                                <p className="text-gray-600 text-sm mb-4">
                                    Try different keywords or check spelling.
                                </p>
                                {categories.length > 0 && (
                                    <div>
                                        <p className="text-xs text-gray-500 mb-2">Browse categories:</p>
                                        <div className="flex flex-wrap gap-2 justify-center">
                                            {categories.slice(0, 4).map(category => (
                                                <button
                                                    key={category}
                                                    onClick={() => handleCategoryChange(category)}
                                                    className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs rounded-full hover:bg-gray-200"
                                                >
                                                    {category}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {results.map(product => (
                                    <SearchProductCard
                                        key={product.id}
                                        product={product}
                                        showRelevance={true}
                                        layout="horizontal"
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SearchResults;